import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { accessibilityPrefs, notificationPrefs, privacyPrefs } from '../data/settings';
type Flags = Record<string, boolean>;
function defaults(list: {
  id: string;
  defaultOn: boolean;
}[]): Flags {
  return list.reduce<Flags>((acc, item) => {
    acc[item.id] = item.defaultOn;
    return acc;
  }, {});
}
interface SettingsValue {
  notifications: Flags;
  toggleNotification: (id: string) => void;
  privacy: Flags;
  togglePrivacy: (id: string) => void;
  accessibility: Flags;
  toggleAccessibility: (id: string) => void;
  twoFactor: boolean;
  setTwoFactor: (value: boolean) => void;
  readNotifications: string[];
  markAllRead: () => void;
  isRead: (id: string) => boolean;
  signedOutSessions: string[];
  signOutSession: (id: string) => void;
  signOutOthers: () => void;
}
const SettingsContext = createContext<SettingsValue | null>(null);
export function SettingsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [notifications, setNotifications] = useState<Flags>(() => defaults(notificationPrefs));
  const [privacy, setPrivacy] = useState<Flags>(() => defaults(privacyPrefs));
  const [accessibility, setAccessibility] = useState<Flags>(() => defaults(accessibilityPrefs));
  const [twoFactor, setTwoFactor] = useState(false);
  const [readNotifications, setRead] = useState<string[]>([]);
  const [signedOutSessions, setSignedOut] = useState<string[]>([]);
  const toggle = useCallback((setter: React.Dispatch<React.SetStateAction<Flags>>, id: string) => setter(prev => ({
    ...prev,
    [id]: !prev[id]
  })), []);
  const value = useMemo<SettingsValue>(() => ({
    notifications,
    toggleNotification: id => toggle(setNotifications, id),
    privacy,
    togglePrivacy: id => toggle(setPrivacy, id),
    accessibility,
    toggleAccessibility: id => toggle(setAccessibility, id),
    twoFactor,
    setTwoFactor,
    readNotifications,
    markAllRead: () => setRead(['*']),
    isRead: id => readNotifications.includes('*') || readNotifications.includes(id),
    signedOutSessions,
    signOutSession: id => setSignedOut(prev => [...prev, id]),
    signOutOthers: () => setSignedOut(['s2', 's3'])
  }), [notifications, privacy, accessibility, twoFactor, readNotifications, signedOutSessions, toggle]);
  return <SettingsContext.Provider value={value} data-id="element-1268" data-mp="jik2hp76bjcf">{children}</SettingsContext.Provider>;
}
export function useSettings(): SettingsValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside SettingsProvider');
  return ctx;
}